import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, LockOutlined, HelpOutlineOutlined,
    ChatOutlined, EmailOutlined, PhoneOutlined,
    KeyboardArrowDownOutlined, KeyboardArrowUpOutlined,
    SearchOutlined, ArticleOutlined, TrendingUpOutlined,
    PaymentsOutlined, AccountBalanceOutlined, SecurityOutlined,
    WhatsApp,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    blue:    '#007AFF',
    orange:  '#FF9500',
    red:     '#FF4D6A',
};

const CONTACT_OPTIONS = [
    { label: 'Live Chat',  sub: 'Avg reply: 2 min',  icon: ChatOutlined,   color: B.purple, online: true  },
    { label: 'WhatsApp',   sub: 'Quick support',      icon: WhatsApp,       color: '#25D366', online: true  },
    { label: 'Email Us',   sub: 'Reply within 24hr', icon: EmailOutlined,  color: B.blue,   online: true  },
    { label: 'Call Us',    sub: 'Mon–Fri · 8am–6pm', icon: PhoneOutlined,  color: B.green,  online: false },
];

const CATEGORIES = [
    { label: 'Credit Score',   emoji: '📊', icon: TrendingUpOutlined,     color: B.purple },
    { label: 'Loans',          emoji: '💰', icon: PaymentsOutlined,       color: B.green  },
    { label: 'Payments',       emoji: '💳', icon: AccountBalanceOutlined, color: B.blue   },
    { label: 'Security',       emoji: '🛡️', icon: SecurityOutlined,       color: B.red    },
];

const FAQS = [
    {
        question: 'What is a credit score and why does it matter?',
        answer: 'A credit score is a number that shows how reliable you are at repaying borrowed money. In Nigeria, a good score helps you access loans, mortgages, and credit cards with better rates from banks and lenders. Think of it as your financial reputation.',
        category: 'Credit Score',
    },
    {
        question: 'How can NG Credit help me build my credit score?',
        answer: 'Through the Credit Builder feature, NG Credit reports your on-time payments to local credit bureaus. As you consistently make repayments, your positive behaviour is recorded — steadily improving your credit history and score over time.',
        category: 'Credit Score',
    },
    {
        question: 'How long does it take to build a good credit score?',
        answer: 'With regular on-time payments and responsible spending, you can see meaningful improvements within a few months. The more consistent you are, the stronger your financial reputation becomes. Most users see a 50–100 point improvement within 6 months.',
        category: 'Credit Score',
    },
    {
        question: 'What happens if I miss a loan payment?',
        answer: 'Missing a payment negatively affects your credit score since payment history is the biggest factor. Pay it off as soon as possible and return to your regular schedule. One missed payment won\'t ruin your score, but a pattern will.',
        category: 'Loans',
    },
    {
        question: 'How do I check my credit score in the app?',
        answer: 'Navigate to the Dashboard tab from the bottom nav. Your current score, score history chart, and a full breakdown of all score factors are displayed there. Your score updates every 30 days.',
        category: 'Credit Score',
    },
    {
        question: 'How do I fund my wallet?',
        answer: 'Go to Wallet → Add Money. You can fund via Bank Transfer (instant, free), Debit/Credit Card (1.5% fee), USSD (works without internet), or QR Code (any banking app). Funds reflect instantly for most methods.',
        category: 'Payments',
    },
    {
        question: 'Why was my transaction declined?',
        answer: 'Transactions can be declined due to insufficient balance, incorrect PIN, network issues, or your card controls. Check your card settings under the Card tab and ensure online payments are enabled. If the issue persists, contact support.',
        category: 'Payments',
    },
    {
        question: 'Is my money and data safe on NG Credit?',
        answer: 'Yes. NG Credit uses 256-bit AES encryption for all data. Deposits are insured by the NDIC up to ₦5,000,000 per depositor. We are licensed by the CBN and never store your card details on our servers.',
        category: 'Security',
    },
];

const HelpCentre: React.FC = () => {
    const navigate  = useNavigate();
    const theme     = useTheme();
    const isDark    = theme.palette.mode === 'dark';

    const [openFaq,   setOpenFaq]   = useState<number | null>(null);
    const [search,    setSearch]    = useState('');
    const [activecat, setActiveCat] = useState('All');

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const filteredFaqs = FAQS.filter(f => {
        const matchSearch = !search ||
            f.question.toLowerCase().includes(search.toLowerCase()) ||
            f.answer.toLowerCase().includes(search.toLowerCase());
        const matchCat = activecat === 'All' || f.category === activecat;
        return matchSearch && matchCat;
    });

    const cats = ['All', ...CATEGORIES.map(c => c.label)];

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.purple, .12)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={() => navigate(-1)}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box flex={1}>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>NG Credit</Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Help Centre
                        </Typography>
                    </Box>
                    <Box sx={{ width: 40, height: 40, borderRadius: '12px', background: alpha(B.purple, .2), border: `1px solid ${alpha(B.light, .3)}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <HelpOutlineOutlined sx={{ color: B.light, fontSize: 20 }} />
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── Contact options 2×2 grid ── */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5, mb: 2 }}>
                    {CONTACT_OPTIONS.map(opt => {
                        const Icon = opt.icon;
                        return (
                            <Box key={opt.label} sx={{
                                ...glassCard, p: 2, cursor: 'pointer',
                                transition: 'all .18s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 12px 32px ${alpha(opt.color, .2)}` },
                            }}>
                                <Box sx={{ width: 42, height: 42, borderRadius: '12px', background: alpha(opt.color, .12), border: `1px solid ${alpha(opt.color, .2)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.25 }}>
                                    <Icon sx={{ color: opt.color, fontSize: 20 }} />
                                </Box>
                                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.85rem' }}>{opt.label}</Typography>
                                <Box display="flex" alignItems="center" gap={.5} mt={.3}>
                                    {opt.online && (
                                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: B.green, boxShadow: `0 0 4px ${B.green}`, flexShrink: 0 }} />
                                    )}
                                    <Typography sx={{ color: textFaint, fontSize: '.68rem' }}>{opt.sub}</Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Browse by topic ── */}
                <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                    <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', mb: 2 }}>Browse by Topic</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                        {CATEGORIES.map(cat => {
                            const Icon = cat.icon;
                            return (
                                <Box key={cat.label} onClick={() => setActiveCat(activecat === cat.label ? 'All' : cat.label)} sx={{
                                    p: 1.5, borderRadius: '14px', cursor: 'pointer', textAlign: 'center',
                                    background: activecat === cat.label ? alpha(cat.color, .12) : alpha(isDark ? '#fff' : B.dark, .04),
                                    border: `1.5px solid ${activecat === cat.label ? alpha(cat.color, .35) : dividerC}`,
                                    transition: 'all .18s',
                                }}>
                                    <Typography sx={{ fontSize: '1.3rem', mb: .5 }}>{cat.emoji}</Typography>
                                    <Typography sx={{ color: activecat === cat.label ? cat.color : textMid, fontSize: '.62rem', fontWeight: 700, lineHeight: 1.2 }}>{cat.label}</Typography>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>

                {/* ── FAQ section ── */}
                <Box sx={{ ...glassCard, overflow: 'hidden' }}>
                    {/* header + search */}
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
                        <Box display="flex" alignItems="center" gap={1} mb={1.75}>
                            <ArticleOutlined sx={{ color: B.purple, fontSize: 18 }} />
                            <Typography sx={{ fontWeight: 700, fontSize: '.95rem', color: textPri, letterSpacing: '-.02em' }}>
                                Frequently Asked Questions
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, background: alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${dividerC}`, borderRadius: '12px', px: 1.5, py: 1 }}>
                            <SearchOutlined sx={{ color: textFaint, fontSize: 16 }} />
                            <input
                                placeholder="Search questions…"
                                value={search}
                                onChange={e => { setSearch(e.target.value); setOpenFaq(null); }}
                                style={{
                                    flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                    color: textPri, fontSize: '.82rem', fontFamily: "'DM Sans', system-ui, sans-serif",
                                }}
                            />
                            {search && (
                                <Typography onClick={() => setSearch('')} sx={{ color: textFaint, fontSize: '.75rem', cursor: 'pointer', '&:hover': { color: textPri } }}>✕</Typography>
                            )}
                        </Box>
                    </Box>

                    {/* category filter pills */}
                    <Box sx={{ px: 2.5, pb: 1.5, display: 'flex', gap: 1, overflowX: 'auto', '&::-webkit-scrollbar': { display: 'none' } }}>
                        {cats.map(c => (
                            <Box key={c} onClick={() => setActiveCat(c)} sx={{
                                flexShrink: 0, px: 1.5, py: .5, borderRadius: '20px', cursor: 'pointer',
                                background: activecat === c ? `linear-gradient(135deg, ${B.purple}, ${B.light})` : alpha(isDark ? '#fff' : B.dark, .05),
                                color: activecat === c ? '#fff' : textFaint,
                                border: `1px solid ${activecat === c ? 'transparent' : dividerC}`,
                                fontSize: '.7rem', fontWeight: 600, transition: 'all .15s',
                                boxShadow: activecat === c ? `0 4px 12px ${alpha(B.purple, .3)}` : 'none',
                            }}>
                                {c}
                            </Box>
                        ))}
                    </Box>

                    <Divider sx={{ mx: 2.5, borderColor: dividerC }} />

                    {/* FAQ items */}
                    {filteredFaqs.length === 0 ? (
                        <Box sx={{ py: 4, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '1.5rem', mb: 1 }}>🔍</Typography>
                            <Typography sx={{ color: textMid, fontWeight: 600, fontSize: '.85rem' }}>No results found</Typography>
                            <Typography sx={{ color: textFaint, fontSize: '.75rem', mt: .5 }}>Try different keywords or browse by topic</Typography>
                        </Box>
                    ) : (
                        filteredFaqs.map((faq, i) => (
                            <React.Fragment key={i}>
                                {i > 0 && <Divider sx={{ mx: 2.5, borderColor: dividerC }} />}
                                <Box sx={{ px: 2.5, py: 1.75, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .02) } }}
                                     onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                    <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
                                        <Typography sx={{ color: textPri, fontWeight: 600, fontSize: '.84rem', lineHeight: 1.45, flex: 1 }}>
                                            {faq.question}
                                        </Typography>
                                        {openFaq === i
                                            ? <KeyboardArrowUpOutlined sx={{ color: B.purple, fontSize: 20, flexShrink: 0, mt: .15 }} />
                                            : <KeyboardArrowDownOutlined sx={{ color: textFaint, fontSize: 20, flexShrink: 0, mt: .15 }} />
                                        }
                                    </Box>
                                    {openFaq === i && (
                                        <Box sx={{ mt: 1.25, pt: 1.25, borderTop: `1px solid ${dividerC}` }}>
                                            <Typography sx={{ color: textMid, fontSize: '.8rem', lineHeight: 1.65 }}>
                                                {faq.answer}
                                            </Typography>
                                            <Box sx={{ mt: 1.5, display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.purple, .08), border: `1px solid ${alpha(B.purple, .18)}`, borderRadius: 20, px: 1.25, py: .4 }}>
                                                <Typography sx={{ color: B.light, fontSize: '.65rem', fontWeight: 600 }}>📂 {faq.category}</Typography>
                                            </Box>
                                        </Box>
                                    )}
                                </Box>
                            </React.Fragment>
                        ))
                    )}

                    {/* Still need help CTA */}
                    <Box sx={{ px: 2.5, py: 2.5, borderTop: `1px solid ${dividerC}`, textAlign: 'center' }}>
                        <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1.5 }}>
                            Can't find what you're looking for?
                        </Typography>
                        <Button variant="contained"
                                sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 3, py: 1, textTransform: 'none', fontWeight: 700, fontSize: '.82rem', boxShadow: `0 8px 24px ${alpha(B.purple, .35)}`, '&:hover': { boxShadow: `0 12px 32px ${alpha(B.purple, .5)}` } }}>
                            Chat with Support
                        </Button>
                    </Box>
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default HelpCentre;